import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, MenuItem, Paper, Stack, Switch, TextField, Typography } from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import AuthImportPanel, { ImportedEvent, ImportedGuest } from '../../components/groups/AuthImportPanel';
import TimeZonePicker from '../../components/groups/TimeZonePicker';
import DateQuestion from '../../components/groups/dates/DateQuestion';
import { checkNewTripDates } from '../../lib/tripDates';
import type { GroupInviteeInput } from '../../types/groups';

function slugify(value: string) {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function CreateGroup() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingInvitees, setPendingInvitees] = useState<GroupInviteeInput[]>([]);
  const [form, setForm] = useState({
    title: '',
    slug: '',
    subtitle: '',
    locationName: '',
    dateStart: '',
    dateEnd: '',
    // Starts EMPTY and is required. Pre-filling the host's own zone is the bug
    // live QA found: a Lisbon trip created from Chicago saved as Chicago, and
    // every itinerary time and change notice then read in Chicago time.
    timeZone: '',
    heroImageUrl: '',
    story: '',
    externalRsvpUrl: '',
    externalRsvpLabel: '',
    accessMode: 'invite_only',
    published: false,
  });

  // The dates are checked wherever they came from (typed, or filled in by the event import): an unfinished year is refused, a far
  // year or a very long trip is a question the host answers before Create. See lib/tripDates (the Trip dates card uses the same rules).
  const [confirmedDates, setConfirmedDates] = useState<string | null>(null);
  const dateCheck = checkNewTripDates(form);
  const datesBlocked = Boolean(dateCheck.errors.start || dateCheck.errors.end || (dateCheck.question && confirmedDates !== dateCheck.question));

  const update = (key: keyof typeof form) => (event: any) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'title' && !prev.slug ? { slug: slugify(value) } : {}),
    }));
  };

  const normalizeRsvpStatus = (value?: string | null) => {
    const status = (value || '').trim().toLowerCase();
    if (['going', 'yes', 'attending', 'confirmed'].includes(status)) return 'going';
    if (['maybe'].includes(status)) return 'maybe';
    if (['declined', 'no', 'not_going', "can't go", 'cant go'].includes(status)) return 'declined';
    return 'unknown';
  };

  const inviteesFromImport = (guests: ImportedGuest[]): GroupInviteeInput[] =>
    guests
      .filter((guest) => guest.name?.trim())
      .map((guest) => ({
        fullName: guest.name.trim(),
        email: guest.email || null,
        phone: guest.phone || null,
        rsvpStatus: normalizeRsvpStatus(guest.rsvp_status),
        source: 'partiful',
        /*
          No synthetic export id, for the same reason the CSV parser dropped its
          own (see lib/csv): `partiful-import-${index}-${name}` keys a guest by
          their POSITION IN THE EXPORT, so re-exporting after one guest drops off
          re-keys everyone below them and the re-import matches nothing.

          It got worse once the import gained a confirmation step. An export id
          is the strongest match key there is, so a row carrying one is accepted
          without asking the host — and two guests who genuinely share a name can
          land on the same manufactured id and be silently merged. An id that is
          not an identity is worse than no id.

          The phone fallback goes for the same reason: a phone number is a
          household, not a person, which is exactly why a phone match is never
          auto-accepted. Copying it into the export id laundered it past that
          rule. An email address is a real identity, so it stays; a guest with
          neither goes in with none, and the preview asks about them by name.
        */
        sourceExternalId: guest.email || null,
      }));

  const applyImport = (ev: ImportedEvent, guests: ImportedGuest[]) => {
    setForm((prev) => ({
      ...prev,
      title: ev.title || prev.title,
      slug: prev.slug || (ev.title ? slugify(ev.title) : prev.slug),
      subtitle: ev.subtitle || prev.subtitle,
      locationName: ev.locationName || prev.locationName,
      dateStart: ev.dateStart ? ev.dateStart.slice(0, 10) : prev.dateStart,
      dateEnd: ev.dateEnd ? ev.dateEnd.slice(0, 10) : prev.dateEnd,
      story: ev.story || prev.story,
      externalRsvpUrl: ev.externalRsvpUrl || prev.externalRsvpUrl,
      externalRsvpLabel: prev.externalRsvpLabel || (ev.externalRsvpUrl ? 'RSVP on Partiful' : prev.externalRsvpLabel),
    }));
    setPendingInvitees(inviteesFromImport(guests));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const group = await groupsApi.createGroup({
        ...form,
        dateStart: form.dateStart || undefined,
        dateEnd: form.dateEnd || undefined,
      });
      if (pendingInvitees.length) {
        await groupsApi.importInvitees(group.id, pendingInvitees);
      }
      navigate(`/groups/${group.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create group');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto' }}>
      <Button onClick={() => navigate('/groups')} sx={{ mb: 2 }}>Back to Groups</Button>
      <Typography variant="h4" fontWeight={800} gutterBottom>Create Group</Typography>
      <Box sx={{ mb: 2 }}>
        <AuthImportPanel onImported={applyImport} />
      </Box>
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {pendingInvitees.length ? (
            <Alert severity="success">
              {pendingInvitees.length} imported guest{pendingInvitees.length === 1 ? '' : 's'} will be saved with this group.
            </Alert>
          ) : null}
          <Alert severity="info">
            Groups use the shared Tripsby concierge by default. After creation, open the Concierge tab to sync event context or provision a dedicated SMS number.
          </Alert>
          <TextField label="Title" value={form.title} onChange={update('title')} required />
          <TextField label="Slug" value={form.slug} onChange={update('slug')} helperText="Public page will live at /g/your-slug" required />
          <TextField label="Subtitle" value={form.subtitle} onChange={update('subtitle')} />
          <TextField label="Location" value={form.locationName} onChange={update('locationName')} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Start date" type="date" value={form.dateStart} onChange={update('dateStart')} InputLabelProps={{ shrink: true }}
              error={Boolean(dateCheck.errors.start)} helperText={dateCheck.errors.start}
            />
            <TextField
              label="End date" type="date" value={form.dateEnd} onChange={update('dateEnd')} InputLabelProps={{ shrink: true }}
              error={Boolean(dateCheck.errors.end)} helperText={dateCheck.errors.end}
            />
          </Box>
          {dateCheck.question ? (
            <DateQuestion
              question={dateCheck.question}
              confirmed={confirmedDates === dateCheck.question}
              onConfirm={(on) => setConfirmedDates(on ? dateCheck.question : null)}
              disabled={saving}
            />
          ) : null}
          <TimeZonePicker
            value={form.timeZone}
            onChange={(zone) => setForm((prev) => ({ ...prev, timeZone: zone }))}
            required
            helperText="Required. Where the trip happens (e.g. Europe/Lisbon). Every itinerary time is entered and shown in this zone."
          />
          <TextField label="Hero image URL" value={form.heroImageUrl} onChange={update('heroImageUrl')} />
          <TextField label="Story / intro" multiline minRows={4} value={form.story} onChange={update('story')} />
          <TextField
            label="External RSVP URL"
            value={form.externalRsvpUrl}
            onChange={update('externalRsvpUrl')}
            placeholder="https://partiful.com/e/..."
            helperText="Link to a third-party RSVP (Partiful, The Knot, Zola). Leave blank to skip RSVP."
          />
          <TextField
            label="RSVP button label"
            value={form.externalRsvpLabel}
            onChange={update('externalRsvpLabel')}
            placeholder="RSVP on Partiful"
          />
          <TextField
            select
            label="Page access"
            value={form.accessMode}
            onChange={update('accessMode')}
            helperText="Invite-only hides event details and room blocks unless the guest opens a generated invite link."
          >
            <MenuItem value="invite_only">Invite-only</MenuItem>
            <MenuItem value="public">Public</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch checked={form.published} onChange={update('published')} />
            <Typography>Publish page</Typography>
          </Box>
          <Box>
            <Button variant="contained" onClick={save} disabled={saving || !form.title || !form.slug || !form.timeZone || datesBlocked}>
              {saving ? 'Creating...' : 'Create Group'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
